// apps/web/src/lib/estoque/pesagem.ts — Fase 13, Plano 30 (EST-15, D-01, D-28, T-13-30A).
// Máquina de estados PURA da tela de pesagem de tinta no tablet (Lote -> Tinta -> Peso -> Sucesso), o ciclo da chave de
// idempotência (uma por tentativa; mantida em retry de rede, renovada em "Nova saída") e os textos do UI-SPEC.
// A autorização, o saldo e as regras de alto giro são do servidor (13-26); aqui só se evita o round-trip óbvio.
import { MOTIVOS_PERDA } from '@pieltec/contracts';
import type { IdentificacaoTinta, ModoPesagem, MotivoPerda, RegistrarPesagemInput, ResumoTintaLote, TintaCandidata } from '@pieltec/contracts';
import { aplicarTecla, exibirPeso, pesoDoTexto, validarPeso } from './teclado-peso';

export const PERMISSAO_PESAGEM = 'estoque:movimentar';

export const MENSAGEM_SEM_CONEXAO = 'Sem conexão. A pesagem não foi registrada. Tente de novo.';
export const MENSAGEM_CODIGO_NAO_ENCONTRADO = 'Código não encontrado. Confira a etiqueta e tente de novo.';
export const MENSAGEM_FALHA_PESAGEM = 'Não foi possível registrar a pesagem. Nada foi alterado.';

export const ROTULOS_MODO_PESAGEM: Readonly<Record<ModoPesagem, string>> = {
  SAIDA: 'Saída',
  DEVOLUCAO: 'Devolução',
  PERDA: 'Perda',
};

export const ROTULOS_BOTAO_FINAL: Readonly<Record<ModoPesagem, string>> = {
  SAIDA: 'Registrar saída',
  DEVOLUCAO: 'Registrar devolução',
  PERDA: 'Registrar perda',
};

export const MENSAGEM_ALTO_GIRO_SEM_DEVOLUCAO = 'Esta cor não retorna ao estoque. Registre só a saída.';

export const ROTULOS_MOTIVO_PERDA: Readonly<Record<MotivoPerda, string>> = {
  VENCIDA: 'Vencida',
  CONTAMINADA: 'Contaminada',
  DERRAMAMENTO: 'Derramamento',
  BORRA: 'Borra',
  OUTRO: 'Outro',
};

export const MOTIVOS_PERDA_TINTA: readonly MotivoPerda[] = MOTIVOS_PERDA;

export type EtapaPesagem = 'LOTE' | 'TINTA' | 'PESO' | 'SUCESSO';

export interface SucessoPesagem {
  modo: ModoPesagem;
  pesoTexto: string;
  tintaCodigo: string;
  lote: string | null;
  /** Rótulo do motivo da perda (só no modo PERDA). */
  motivo: string | null;
  /** O servidor respondeu 200: a chave já tinha um movimento (nenhum movimento novo). */
  repetido: boolean;
  /** Para "Devolver sobra deste lote": só existe quando houve saída por lote de tinta que retorna ao estoque. */
  podeDevolverSobra: boolean;
}

export interface EstadoPesagem {
  modo: ModoPesagem;
  etapa: EtapaPesagem;
  /** De onde veio o lote: lido pelo QR, "alto giro (sem lote)" ou "perda sem lote". */
  origem: 'LOTE' | 'ALTO_GIRO' | 'SEM_LOTE' | null;
  identificacao: IdentificacaoTinta | null;
  tinta: TintaCandidata | null;
  pesoTexto: string;
  motivoPerda: MotivoPerda | null;
  detalhes: string;
  justificativa: string;
  /** Consumo da tinta no lote lido (Devolução): "saiu {S} kg · já devolvido {D} kg". */
  resumo: ResumoTintaLote | null;
  /** Devolução maior que a saída apontada pelo servidor (422 JUSTIFICATIVA_OBRIGATORIA), quando o resumo local não bastou. */
  excedenteServidor: { devolucaoKg: number; saidaKg: number } | null;
  /** Chave de idempotência da tentativa em curso; null até o primeiro envio e após qualquer mudança do que será enviado. */
  chave: string | null;
  enviando: boolean;
  erro: string | null;
  sucesso: SucessoPesagem | null;
}

export function estadoInicialPesagem(modo: ModoPesagem = 'SAIDA'): EstadoPesagem {
  return {
    modo,
    etapa: 'LOTE',
    origem: null,
    identificacao: null,
    tinta: null,
    pesoTexto: '',
    motivoPerda: null,
    detalhes: '',
    justificativa: '',
    resumo: null,
    excedenteServidor: null,
    chave: null,
    enviando: false,
    erro: null,
    sucesso: null,
  };
}

export type AcaoPesagem =
  | { tipo: 'MODO'; modo: ModoPesagem }
  | { tipo: 'IDENTIFICADO'; identificacao: IdentificacaoTinta }
  | { tipo: 'SEM_LOTE'; origem: 'ALTO_GIRO' | 'SEM_LOTE' }
  | { tipo: 'TROCAR_LOTE' }
  | { tipo: 'TINTA'; tinta: TintaCandidata }
  | { tipo: 'CONTINUAR_COM_TINTA' }
  | { tipo: 'TROCAR_TINTA' }
  | { tipo: 'TECLA'; tecla: string }
  | { tipo: 'MOTIVO'; motivo: MotivoPerda }
  | { tipo: 'DETALHES'; texto: string }
  | { tipo: 'JUSTIFICATIVA'; texto: string }
  | { tipo: 'RESUMO'; resumo: ResumoTintaLote | null }
  | { tipo: 'EXIGE_JUSTIFICATIVA'; devolucaoKg: number; saidaKg: number }
  | { tipo: 'ENVIANDO'; chave: string }
  | { tipo: 'SUCESSO'; sucesso: SucessoPesagem }
  | { tipo: 'FALHA'; mensagem: string; manterChave: boolean }
  | { tipo: 'NOVA_SAIDA' }
  | { tipo: 'DEVOLVER_SOBRA' }
  | { tipo: 'CONCLUIR' };

/** Tinta pré-selecionada ao ler o lote: a primeira candidata da cor do item (o servidor põe as com saldo antes). */
function tintaPreSelecionada(identificacao: IdentificacaoTinta): TintaCandidata | null {
  return identificacao.tintasCandidatas[0] ?? null;
}

/** Mudança do que será enviado: a chave da tentativa anterior não pode ser reaproveitada (evita repetir o movimento antigo). */
function mudouPayload(estado: EstadoPesagem, mudancas: Partial<EstadoPesagem>): EstadoPesagem {
  return { ...estado, ...mudancas, chave: null, erro: null, excedenteServidor: null };
}

/** Ações que podem chegar com um envio em curso: o desfecho do envio e dados de apoio que não entram no payload. */
const ACOES_DURANTE_ENVIO: ReadonlySet<AcaoPesagem['tipo']> = new Set(['SUCESSO', 'FALHA', 'EXIGE_JUSTIFICATIVA', 'RESUMO']);

export function reduzirPesagem(estado: EstadoPesagem, acao: AcaoPesagem): EstadoPesagem {
  // WR-C03: enquanto a requisição está em curso nada que mude o payload pode zerar a chave de idempotência (o reenvio após
  // rede/5xx precisa da MESMA chave) nem trocar a seleção por baixo de um SUCESSO que ainda vai chegar.
  if (estado.enviando && !ACOES_DURANTE_ENVIO.has(acao.tipo)) return estado;
  switch (acao.tipo) {
    case 'MODO':
      // Devolução exige lote; sem lote lido (alto giro / perda sem lote) volta à etapa 1 em vez de ficar num beco.
      if (acao.modo !== 'PERDA' && estado.identificacao === null && estado.etapa !== 'LOTE' && estado.origem !== null) {
        return mudouPayload(estado, { modo: acao.modo, etapa: 'LOTE', origem: null, tinta: null, resumo: null });
      }
      return mudouPayload(estado, { modo: acao.modo });
    case 'IDENTIFICADO':
      return mudouPayload(estado, {
        etapa: 'TINTA',
        origem: 'LOTE',
        identificacao: acao.identificacao,
        tinta: tintaPreSelecionada(acao.identificacao),
        resumo: null,
      });
    case 'SEM_LOTE':
      return mudouPayload(estado, { etapa: 'TINTA', origem: acao.origem, identificacao: null, tinta: null, resumo: null });
    case 'TROCAR_LOTE':
      return mudouPayload(estado, { etapa: 'LOTE', origem: null, identificacao: null, tinta: null, resumo: null });
    case 'TINTA':
      return mudouPayload(estado, { etapa: 'PESO', tinta: acao.tinta, resumo: null });
    case 'CONTINUAR_COM_TINTA':
      return estado.tinta ? mudouPayload(estado, { etapa: 'PESO' }) : estado;
    case 'TROCAR_TINTA':
      return mudouPayload(estado, { etapa: 'TINTA' });
    case 'TECLA':
      return mudouPayload(estado, { pesoTexto: aplicarTecla(estado.pesoTexto, acao.tecla) });
    case 'MOTIVO':
      return mudouPayload(estado, { motivoPerda: acao.motivo });
    case 'DETALHES':
      return mudouPayload(estado, { detalhes: acao.texto });
    case 'JUSTIFICATIVA':
      // Não limpa o excedente do servidor: o alerta continua até o peso mudar.
      return { ...estado, justificativa: acao.texto, chave: null, erro: null };
    case 'RESUMO':
      return { ...estado, resumo: acao.resumo };
    case 'EXIGE_JUSTIFICATIVA':
      return {
        ...estado,
        enviando: false,
        erro: null,
        chave: null,
        excedenteServidor: { devolucaoKg: acao.devolucaoKg, saidaKg: acao.saidaKg },
      };
    case 'ENVIANDO':
      return { ...estado, enviando: true, erro: null, chave: acao.chave };
    case 'SUCESSO':
      return { ...estado, enviando: false, erro: null, etapa: 'SUCESSO', sucesso: acao.sucesso };
    case 'FALHA':
      // Falha de rede mantém a chave (a mesma tentativa); rejeição definitiva do servidor libera para uma nova.
      return { ...estado, enviando: false, erro: acao.mensagem, chave: acao.manterChave ? estado.chave : null };
    case 'NOVA_SAIDA':
      return { ...estadoInicialPesagem('SAIDA') };
    case 'DEVOLVER_SOBRA':
      return {
        ...estado,
        modo: 'DEVOLUCAO',
        etapa: 'PESO',
        pesoTexto: '',
        motivoPerda: null,
        detalhes: '',
        justificativa: '',
        resumo: null,
        excedenteServidor: null,
        chave: null,
        enviando: false,
        erro: null,
        sucesso: null,
      };
    case 'CONCLUIR':
      return estadoInicialPesagem('SAIDA');
  }
}

/** Chave da tentativa: gera uma vez e mantém em retry; só é renovada quando o estado volta a `null` (nova pesagem). */
export function chaveDaTentativa(estado: EstadoPesagem, gerar: () => string): string {
  return estado.chave ?? gerar();
}

/** Lote que vai no payload: tinta de alto giro nunca leva lote na saída (regra do servidor). */
export function loteDoPayload(estado: EstadoPesagem): string | null {
  if (estado.modo === 'SAIDA' && estado.tinta?.altoGiro) return null;
  return estado.identificacao?.lote.id ?? null;
}

/** Modo indisponível para a tinta escolhida: tinta de alto giro não tem devolução. */
export function modoDesabilitado(estado: EstadoPesagem, modo: ModoPesagem): boolean {
  return modo === 'DEVOLUCAO' && estado.tinta?.altoGiro === true;
}

function arredondar4(valor: number): number {
  return Math.round(valor * 10000) / 10000;
}

/** Devolução acima do saído do lote (aviso, não trava): do resumo local ou do 422 do servidor. */
export function excedenteDevolucao(estado: EstadoPesagem): { devolucaoKg: number; saidaKg: number } | null {
  if (estado.modo !== 'DEVOLUCAO') return null;
  const peso = pesoDoTexto(estado.pesoTexto);
  if (estado.resumo !== null && peso !== null && peso > 0) {
    const devolucaoKg = arredondar4(estado.resumo.devolvidoKg + peso);
    if (devolucaoKg > arredondar4(estado.resumo.saidaKg)) return { devolucaoKg, saidaKg: estado.resumo.saidaKg };
    return null;
  }
  return estado.excedenteServidor;
}

/** Botão final habilitado: tudo válido e nenhum envio em curso (toque duplo). */
export function podeRegistrar(estado: EstadoPesagem): boolean {
  if (estado.enviando || estado.etapa !== 'PESO' || estado.tinta === null) return false;
  if (validarPeso(estado.pesoTexto) !== null) return false;
  if (estado.modo === 'PERDA') {
    if (estado.motivoPerda === null) return false;
    return estado.motivoPerda !== 'OUTRO' || estado.detalhes.trim() !== '';
  }
  if (modoDesabilitado(estado, estado.modo)) return false;
  if (estado.modo === 'SAIDA' && estado.tinta.altoGiro) return true;
  if (estado.identificacao === null) return false;
  if (excedenteDevolucao(estado) !== null && estado.justificativa.trim() === '') return false;
  return true;
}

/** "Devolver sobra deste lote": só após saída por lote de tinta que retorna ao estoque. */
export function podeDevolverSobra(estado: EstadoPesagem): boolean {
  return estado.modo === 'SAIDA' && estado.identificacao !== null && estado.tinta !== null && !estado.tinta.altoGiro;
}

export function montarPayloadPesagem(estado: EstadoPesagem, chave: string): RegistrarPesagemInput | null {
  const peso = pesoDoTexto(estado.pesoTexto);
  if (estado.tinta === null || peso === null) return null;
  const payload: RegistrarPesagemInput = {
    modo: estado.modo,
    materialId: estado.tinta.materialId,
    loteId: loteDoPayload(estado),
    quantidadeKg: peso,
    chaveIdempotencia: chave,
    fontePeso: 'MANUAL',
  };
  if (estado.modo === 'DEVOLUCAO' && estado.justificativa.trim() !== '') payload.justificativa = estado.justificativa.trim();
  if (estado.modo === 'PERDA') {
    payload.motivoPerda = estado.motivoPerda;
    if (estado.detalhes.trim() !== '') payload.detalhesPerda = estado.detalhes.trim();
  }
  return payload;
}

function pesoParaTexto(pesoTexto: string): string {
  return exibirPeso(pesoTexto.endsWith(',') ? pesoTexto.slice(0, -1) : pesoTexto);
}

/** Mensagem do cartão de sucesso (role="status"). */
export function textoSucesso(sucesso: SucessoPesagem): string {
  const peso = pesoParaTexto(sucesso.pesoTexto);
  if (sucesso.modo === 'PERDA') return `Perda registrada: ${peso} kg de ${sucesso.tintaCodigo} (${sucesso.motivo ?? ''}).`;
  if (sucesso.modo === 'DEVOLUCAO') return `Devolução registrada: ${peso} kg de ${sucesso.tintaCodigo} no lote ${sucesso.lote ?? ''}.`;
  return `Saída registrada: ${peso} kg de ${sucesso.tintaCodigo} para o lote ${sucesso.lote ?? ''}.`;
}

export type ErroPesagem = { tipo: 'REDE' } | { tipo: 'HTTP'; status: number; corpo: unknown };

export interface ErroInterpretadoPesagem {
  mensagem: string;
  /** true quando o servidor pode ter gravado o movimento (rede/5xx): o retry reenvia a MESMA chave. */
  manterChave: boolean;
  /** 422 JUSTIFICATIVA_OBRIGATORIA: devolução maior que o saído; a tela mostra o alerta e pede a justificativa. */
  excedente?: { devolucaoKg: number; saidaKg: number };
}

export const MENSAGEM_SEM_PERMISSAO = 'Você não tem permissão para esta ação.';
export const MENSAGEM_SESSAO_EXPIRADA = 'Sua sessão expirou. Toque em Trocar operador para entrar de novo.';
export const MENSAGEM_SALDO_INSUFICIENTE = 'Saldo insuficiente. Confira o peso ou registre a entrada da tinta.';
export const MENSAGEM_DETALHES_OBRIGATORIOS = 'Informe os detalhes da perda.';

function numeroOuNulo(valor: unknown): number | null {
  return typeof valor === 'number' && Number.isFinite(valor) ? valor : null;
}

function textoOuNulo(valor: unknown): string | null {
  return typeof valor === 'string' && valor.trim() !== '' ? valor : null;
}

/** Interpreta a falha de rede ou a resposta do servidor em mensagem pt-BR (contrato do UI-SPEC). */
export function mensagemErro(erro: ErroPesagem): ErroInterpretadoPesagem {
  if (erro.tipo === 'REDE') return { mensagem: MENSAGEM_SEM_CONEXAO, manterChave: true };
  const corpo = (erro.corpo && typeof erro.corpo === 'object' ? erro.corpo : {}) as Record<string, unknown>;
  const codigo = textoOuNulo(corpo.codigo);
  const bruta: unknown = Array.isArray(corpo.message) ? (corpo.message as unknown[])[0] : corpo.message;
  const mensagemApi = textoOuNulo(bruta);
  // Quando o servidor pode ter gravado (5xx), o retry reenvia a mesma chave e o servidor devolve o movimento original.
  const manterChave = erro.status >= 500;

  if (erro.status === 401) return { mensagem: MENSAGEM_SESSAO_EXPIRADA, manterChave: false };
  if (erro.status === 403) return { mensagem: MENSAGEM_SEM_PERMISSAO, manterChave: false };
  if (codigo === 'JUSTIFICATIVA_OBRIGATORIA') {
    const devolucaoKg = numeroOuNulo(corpo.devolucaoKg);
    const saidaKg = numeroOuNulo(corpo.saidaKg);
    if (devolucaoKg !== null && saidaKg !== null) {
      return { mensagem: mensagemApi ?? MENSAGEM_FALHA_PESAGEM, manterChave: false, excedente: { devolucaoKg, saidaKg } };
    }
  }
  if (codigo === 'DEVOLUCAO_ALTO_GIRO') return { mensagem: MENSAGEM_ALTO_GIRO_SEM_DEVOLUCAO, manterChave: false };
  if (codigo === 'SALDO_INSUFICIENTE' || (erro.status === 409 && codigo === null)) {
    return { mensagem: mensagemApi ?? MENSAGEM_SALDO_INSUFICIENTE, manterChave: false };
  }
  if (codigo === 'DETALHES_OBRIGATORIOS') return { mensagem: MENSAGEM_DETALHES_OBRIGATORIOS, manterChave: false };
  // Demais 422/404 trazem texto pt-BR pronto do servidor; 400 de validação (inglês) e 5xx ficam com a mensagem padrão.
  if ((erro.status === 422 || erro.status === 404 || codigo !== null) && mensagemApi !== null && erro.status < 500) {
    return { mensagem: mensagemApi, manterChave: false };
  }
  return { mensagem: MENSAGEM_FALHA_PESAGEM, manterChave };
}
